import { FunctionComponent, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { loadPageScripts } from '../scripts';

const COOKIE_CONSENT_KEY = 'cookie_consent';

export function useCookieConsent() {
  const [{ cookie_consent: decision }, setCookie] = useCookies([
    COOKIE_CONSENT_KEY,
  ]);
  const [isReady, setIsReady] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    setHasConsent(decision === 'accepted');
    setIsReady(true);
  }, [decision]);

  function accept() {
    setCookie(COOKIE_CONSENT_KEY, 'accepted', {
      path: '/',
      maxAge: 365 * 24 * 60 * 60,
    });
  }

  function decline() {
    setCookie(COOKIE_CONSENT_KEY, 'declined', {
      path: '/',
      maxAge: 365 * 24 * 60 * 60,
    });
  }

  if (hasConsent) {
    loadPageScripts();
  }

  return {
    isReady,
    decision: isReady ? decision : undefined,
    hasConsent,
    accept,
    decline,
  };
}

export const CookieConsent: FunctionComponent = () => {
  const { isReady, decision, accept, decline } = useCookieConsent();

  if (!isReady || decision) {
    return null;
  }

  return (
    <Banner>
      <Text>
        We use cookies and scripts to improve your experience on our site. By
        accepting, you agree to the use of tracking scripts and analytics.
      </Text>
      <Actions>
        <DeclineButton onClick={decline}>Decline</DeclineButton>
        <AcceptButton onClick={accept}>Accept</AcceptButton>
      </Actions>
    </Banner>
  );
};

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  background: white;
  color: #1a1a2e;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Text = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const DeclineButton = styled.button`
  padding: 8px 20px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  background: transparent;
  color: #1a1a2e;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 0, 0, 0.6);
  }
`;

const AcceptButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  background: #6c63ff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #5a52d5;
  }
`;
